import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/bits";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ROLE_LABELS = { secretaria: "Secretaria", contabilidad: "Contabilidad", maestro: "Maestro" };
const ACTION_LABELS = { view: "Ver", manage: "Crear/Editar", delete: "Eliminar", create: "Crear", approve: "Aprobar" };

export default function Permisos() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const isSuper = user?.role === "superadmin";

  useEffect(() => {
    api.get("/permissions/catalog").then((r) => setCatalog(r.data)).catch((e) => toast.error(formatApiError(e)));
    if (isSuper) {
      api.get("/schools").then((r) => {
        setSchools(r.data);
        if (r.data.length) setSchoolId(r.data[0].id);
      }).catch(() => {});
    }
  }, [isSuper]);

  const loadMatrix = useCallback(() => {
    const config = isSuper && schoolId ? { headers: { "X-School-Id": schoolId } } : {};
    api.get("/permissions/matrix", config).then((r) => setMatrix(r.data.matrix)).catch((e) => toast.error(formatApiError(e)));
  }, [isSuper, schoolId]);
  useEffect(() => { if (!isSuper || schoolId) loadMatrix(); }, [loadMatrix, isSuper, schoolId]);

  const toggle = (role, key) => {
    setMatrix((m) => ({ ...m, [role]: { ...m[role], [key]: !m[role]?.[key] } }));
  };

  const save = async () => {
    try {
      const config = isSuper && schoolId ? { headers: { "X-School-Id": schoolId } } : {};
      await api.put("/permissions/matrix", { matrix }, config);
      toast.success("Matriz de permisos guardada");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!catalog || !matrix) return <p className="text-slate-400 text-sm">Cargando matriz de permisos…</p>;

  return (
    <div data-testid="permissions-page">
      <PageHeader title="Matriz de permisos" subtitle="Define qué puede hacer cada rol en este colegio. El administrador siempre tiene acceso total.">
        {isSuper && (
          <Select value={schoolId} onValueChange={setSchoolId}>
            <SelectTrigger className="w-56" data-testid="permissions-school-select"><SelectValue placeholder="Colegio" /></SelectTrigger>
            <SelectContent>
              {schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button data-testid="save-permissions-button" className="bg-blue-800 hover:bg-blue-700" onClick={save}>
          <Save className="w-4 h-4 mr-1" /> Guardar cambios
        </Button>
      </PageHeader>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="permissions-matrix-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Módulo</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Acción</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-center">
                <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Admin</span>
              </th>
              {catalog.editable_roles.map((r) => (
                <th key={r} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-center">{ROLE_LABELS[r]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {catalog.modules.map((mod) =>
              mod.actions.map((action, ai) => {
                const key = `${mod.key}.${action}`;
                return (
                  <tr key={key} className="border-b border-slate-100 hover:bg-slate-50">
                    {ai === 0 && (
                      <td rowSpan={mod.actions.length} className="px-4 py-2 font-medium text-slate-900 align-top border-r border-slate-100">
                        {mod.label}
                      </td>
                    )}
                    <td className="px-4 py-2 text-slate-600">{ACTION_LABELS[action]}</td>
                    <td className="px-4 py-2 text-center">
                      <input type="checkbox" checked disabled className="accent-blue-800 opacity-60" data-testid={`perm-admin-${key}`} />
                    </td>
                    {catalog.editable_roles.map((r) => (
                      <td key={r} className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          className="accent-blue-800 cursor-pointer"
                          checked={!!matrix[r]?.[key]}
                          onChange={() => toggle(r, key)}
                          data-testid={`perm-${r}-${key}`}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400 mt-3">
        Los cambios aplican a todos los usuarios del colegio con ese rol. El padre/tutor tiene su propio portal fijo.
      </p>
    </div>
  );
}
